import { Link, useLocation } from 'react-router-dom';
import { BRAND, CONTACT, SOCIAL } from '@/lib/constants';
import { FOOTER_LEGAL } from '@/data/navigation';
import { LOGOS } from '@/lib/assets';
import { IconCircle } from '@/components/ui/IconCircle';
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  WhatsAppIcon,
  FacebookIcon,
  LinkedInIcon,
  InstagramIcon,
} from '@/components/icons/ContactIcons';
import { ROUTES } from '@/lib/routes';

const GRAY_FOOTER_ROUTES: string[] = [ROUTES.home, ROUTES.comoFunciona, ROUTES.privacidade];

export function Footer() {
  const { pathname } = useLocation();

  const isGray = GRAY_FOOTER_ROUTES.includes(pathname);
  const bgClass = isGray ? 'bg-brand-darkgray text-brand-purple' : 'bg-brand-purple text-white';

  return (
    <footer>
      <div className={bgClass}>
        {/* Main footer content */}
        <div className="container-page py-10 md:py-14">
          <div className="grid gap-10 md:gap-6 md:grid-cols-[1fr_1.2fr_auto]">
            {/* Left — Logo + brand */}
            <div className="flex flex-col items-start justify-center">
              <img
                src={LOGOS.iconText}
                alt={BRAND.name}
                className={`h-40 md:h-110 w-auto -my-12 ${isGray ? '' : 'brightness-0 invert'}`}
              />
            </div>

            {/* Middle — Contact info */}
            <div className="flex flex-col justify-center gap-6 pt-0 md:pt-14">
              <div className="flex items-center gap-4">
                <IconCircle tone="yellow" size="sm" className="md:h-12 md:w-12">
                  <PhoneIcon className="h-3.5 w-3.5 md:h-5 md:w-5" />
                </IconCircle>
                <div>
                  <p className="text-xs opacity-70">Telefone</p>
                  <p className="text-sm md:text-base font-semibold">{CONTACT.phone}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <IconCircle tone="yellow" size="sm" className="md:h-12 md:w-12">
                  <EnvelopeIcon className="h-3.5 w-3.5 md:h-5 md:w-5" />
                </IconCircle>
                <div>
                  <p className="text-xs opacity-70">E-mail</p>
                  <p className="text-sm md:text-base font-semibold">{CONTACT.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <IconCircle tone="yellow" size="sm" className="md:h-12 md:w-12">
                  <MapPinIcon className="h-3.5 w-3.5 md:h-5 md:w-5" />
                </IconCircle>
                <div>
                  <p className="text-xs opacity-70">Endereço</p>
                  <p className="text-sm md:text-base font-semibold">
                    R. Mariano Torres, 729, 4° andar, &nbsp;Curitiba - PR.
                  </p>
                </div>
              </div>
            </div>

            {/* Right — Social icons 2×2 grid */}
            <div className="flex items-center justify-start md:justify-end pr-0 md:pr-24 pt-0 md:pt-24">
              <div className="grid grid-cols-4 md:grid-cols-2 -ml-1.5 md:ml-0">
                <div className="p-1.5 md:p-4 flex items-center justify-center">
                  <a
                    href={SOCIAL.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook da ARCOLL TECH"
                    className="inline-flex"
                  >
                    <FacebookIcon className="h-10 w-10 md:h-14 md:w-14 text-brand-yellow" />
                  </a>
                </div>
                <div className="p-1.5 md:p-4 flex items-center justify-center">
                  <a
                    href={CONTACT.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="WhatsApp da ARCOLL TECH"
                    className="inline-flex"
                  >
                    <WhatsAppIcon className="h-10 w-10 md:h-14 md:w-14" />
                  </a>
                </div>
                <div className="p-1.5 md:p-4 flex items-center justify-center">
                  <a
                    href={SOCIAL.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn da ARCOLL TECH"
                    className="inline-flex"
                  >
                    <LinkedInIcon className="h-10 w-10 md:h-14 md:w-14 text-brand-yellow md:text-current" />
                  </a>
                </div>
                <div className="p-1.5 md:p-4 flex items-center justify-center">
                  <a
                    href={SOCIAL.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram da ARCOLL TECH"
                    className="inline-flex"
                  >
                    <InstagramIcon className="h-10 w-10 md:h-14 md:w-14 text-brand-purple md:text-brand-yellow" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom band — legal info */}
        <div className={`border-t ${isGray ? 'border-brand-purple/10' : 'border-white/10'}`}>
          <div className="container-page py-5 flex flex-col items-center gap-3 text-center">
            <ul className="flex flex-wrap items-center justify-center gap-4">
              {FOOTER_LEGAL.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`text-xs uppercase tracking-[0.2em] font-semibold
                      underline underline-offset-2 transition-colors ${
                        isGray ? 'hover:text-brand-yellow-text' : 'hover:text-brand-yellow'
                      }`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="text-[11px] opacity-60 leading-relaxed">
              {BRAND.legalName} - {CONTACT.email} - CNPJ: {BRAND.cnpj}
              <br />
              R. Mariano Torres, 729, 4° andar, Curitiba/PR
              {' - '}{CONTACT.address.zip} - Telefone: {CONTACT.phone}
            </p>
          </div>
        </div>
      </div>

    </footer>
  );
}
